<template>
  <div class="single-test">
    <div class="head">
      <div class="response-head-title">{{ test.name }}</div>

      <div v-if="calculateTestStatus && hasPassed" class="passed">
        {{ $tr('responses.tabs.test.has_passed') }}
      </div>

      <div v-if="calculateTestStatus && !hasPassed" class="not-passed">
        {{ $tr('responses.tabs.test.has_not_passed') }}
      </div>

      <div v-if="score.count" class="info">
        <div
          v-if="test.is_print_screen"
          v-tooltip="{ content: $tr('response.preventPrintScreen'), classes: 'tight' }"
          class="anticheating-icon"
        >
          <img src="/static/images/elements/icon-anticheating.svg" />
        </div>
        <span class="count">
          {{ `${score.correct} ${$tr('responses.tabs.test.count')} ${score.count}` }}
        </span>
        <span class="percent">
          {{ `${percentCorrectAnswers} ${$tr('responses.tabs.test.percent')}` }}
        </span>
      </div>
    </div>

    <div v-if="duration" class="duration-block">
      {{ $tr('responses.tabs.test.total_questions_time') }} {{ duration }}
    </div>

    <div class="tabs">
      <div
        v-for="(question, index) in questions"
        :key="question.id"
        class="tab"
        :class="{
          correct: question.is_correct,
          incorrect: question.is_correct === false && !isPartialCorrect(question.answers),
          'partial-correct': question.is_correct === false && isPartialCorrect(question.answers),
          'no-correct-answer': question.is_correct === null,
          selected: selectedQuestion && selectedQuestion.id === question.id
        }"
        @click="selectQuestion(question)"
      >
        <span class="number">{{ index + 1 }}</span>
      </div>
    </div>

    <question v-if="selectedQuestion" :question="selectedQuestion" :one-page-test="onePageTest" />

    <screenshots
      v-if="isAnticheatingActive || screenshots || screenshots.length"
      :is-active="isAnticheatingActive"
      :question="selectedQuestion"
      :screenshots="screenshots"
    />
  </div>
</template>

<script>
import axios from 'axios';
import Question from './Question';
import Screenshots from './Screenshots';
import { mapGetters } from 'vuex';

export default {
  name: 'SingleTestResult',

  components: { Question, Screenshots },

  props: {
    test: {
      type: Object,
      default: () => {}
    },

    responseId: {
      type: String,
      default: ''
    },

    response: {
      type: Object,
      default: () => {}
    },

    vacancyToken: {
      type: String,
      default: ''
    },

    isPublic: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      selectedQuestion: null,
      TYPE_ANSWER: {
        CORRECT: 'correct',
        PARTIALLY_CORRECT: 'partially_correct',
        INCORRECT: 'incorrect',
        NOT_SELECTED: 'not-selected'
      },

      modules: []
    };
  },

  computed: {
    ...mapGetters('candidate_test', ['hasPassed', 'calculateTestStatus']),

    landing() {
      return this.$store.getters['landing/item'];
    },

    onePageTest() {
      if (!this.test) {
        return false;
      }

      return this.test.question_page_limit === 0;
    },

    duration() {
      // let durationOnce = 0;

      // this.questions.forEach(item => {
      //   if (item.duration) {
      //     durationOnce = item.duration;
      //   }
      // });

      // return moment.duration(durationOnce, 'seconds').format('mm:ss', { trim: false });
      let duration = 0;

      if (this.onePageTest) {
        duration = this.questions[0].duration;
      } else {
        this.questions.forEach(x => {
          duration += x.duration;
        });
      }

      return this.$moment.duration(duration, 'seconds').format('HH:mm:ss', { trim: false });
    },

    screenshots() {
      if (!this.test || !this.test.screenshots) {
        return [];
      }
      return this.test.screenshots;
    },

    isAnticheatingActive() {
      if (!this.isPublic) {
        if (!this.modules || !this.modules.length) {
          return false;
        }
        let isActive = false;

        this.modules.forEach(item => {
          if (item.id === 'anticheating') {
            isActive = item.is_active;
          }
        });

        return isActive;
      } else {
        if (!this.landing || !this.landing.active_modules || !this.landing.active_modules.length) {
          return false;
        }

        let isActive = false;
        this.landing.active_modules.forEach(item => {
          if (item.id === 'anticheating') {
            isActive = item.is_active;
          }

          if (item.child_modules && item.child_modules.length) {
            item.child_modules.forEach(submodule => {
              if (submodule.id === 'anticheating') {
                isActive = true;
              }
            });
          }
        });

        return isActive;
      }
    },

    questions() {
      return this.test.questions
        .slice()
        .map(question => {
          // const selected = question.answers.map(answer => answer.is_chosen);
          // const valids = question.answers.map(answer => answer.is_valid);

          switch (question.type) {
            case 'choice':
            case 'multichoice':
              if (question.is_correct === null) {
                question.isCorrect = this.TYPE_ANSWER.NOT_SELECTED;
              } else if (question.is_correct) {
                question.isCorrect = this.TYPE_ANSWER.CORRECT;
              } else if (!question.is_correct && question.answers.some(x => x.is_valid && x.is_correct)) {
                question.isCorrect = this.TYPE_ANSWER.PARTIALLY_CORRECT;
              } else if (!question.is_correct) {
                question.isCorrect = this.TYPE_ANSWER.INCORRECT;
              } else {
                question.isCorrect = this.TYPE_ANSWER.INCORRECT;
              }
              break;
            case 'value':
              if (question.is_correct === null) {
                question.isCorrect = this.TYPE_ANSWER.NOT_SELECTED;
              } else if (question.is_correct) {
                question.isCorrect = this.TYPE_ANSWER.CORRECT;
              } else {
                question.isCorrect = this.TYPE_ANSWER.INCORRECT;
              }
              break;
            default:
              question.isCorrect = this.TYPE_ANSWER.NOT_SELECTED;
          }
          // switch (question.type) {
          //   case 'choice':
          //   case 'multichoice':
          //     if (!valids.some(valid => valid)) {
          //       question.isCorrect = this.TYPE_ANSWER.NOT_SELECTED;
          //     } else if (valids.every((valid, index) => valid === selected[index])) {
          //       question.isCorrect = this.TYPE_ANSWER.CORRECT;
          //     } else if (valids.some((valid, index) => selected[index] && valid === selected[index])) {
          //       question.isCorrect = this.TYPE_ANSWER.PARTIALLY_CORRECT;
          //     } else {
          //       question.isCorrect = this.TYPE_ANSWER.INCORRECT;
          //     }
          //     break;
          //   case 'value':
          //     if (question.is_correct === null) {
          //       question.isCorrect = this.TYPE_ANSWER.NOT_SELECTED;
          //     } else if (question.is_correct) {
          //       question.isCorrect = this.TYPE_ANSWER.CORRECT;
          //     } else {
          //       question.isCorrect = this.TYPE_ANSWER.INCORRECT;
          //     }
          //     break;
          //   default:
          //     question.isCorrect = this.TYPE_ANSWER.NOT_SELECTED;
          // }
          return question;
        })
        .sort((q1, q2) => q1.priority - q2.priority);
    },

    score() {
      let result = {
        count: 0,
        correct: 0
      };

      if (!this.haveQuestions) return result;

      return this.questions.reduce((score, question) => {
        if (!((~['choice', 'multichoice', 'value'].indexOf(question.type) && question.isCorrect !== this.TYPE_ANSWER.NOT_SELECTED) ||
            question.valid_answer
          )
        ) {
          return score;
        }
        score.count++;
        if (question.isCorrect === this.TYPE_ANSWER.CORRECT) score.correct++;
        return score;
      }, result);
    },

    haveQuestions() {
      return this.questions && this.questions.length;
    },

    percentCorrectAnswers() {
      if (
        this.test.is_expired &&
        this.test.result.correct_rate !== undefined &&
        this.test.result.correct_rate !== null
      ) {
        return this.test.result.correct_rate.toFixed(2);
      }

      return Math.round((100 / this.score.count) * this.score.correct);
    }
  },

  async created() {
    if (this.questions.length) this.selectQuestion(this.questions[0]);

    if (!this.isPublic) {
      this.fetchModules();
    } else {
      this.fetchPublicVacancy();
    }

    if (this.$route.query.questionId !== undefined) {
      const question = this.questions.find(x => x.id == this.$route.query.questionId);

      if (question !== undefined) {
        this.selectQuestion(question);
      }
    }
  },

  methods: {
    async selectQuestion(question) {
      if (this.selectedQuestion && this.selectedQuestion.id == question.id) {
        this.selectedQuestion = null;
      } else {
        await this.$nextTick();
        this.selectedQuestion = question;
      }
    },

    fetchModules() {
      if (!this.response || !this.response.vacancy) {
        return;
      }
      axios.get(`/1.0/vacancies/${this.response.vacancy.id}/modules`).then(response => {
        this.modules = response.data.data;
      });
    },

    fetchPublicVacancy() {
      if (this.vacancyToken) {
        this.$store.dispatch('landing/load', this.vacancyToken);
      }
    },

    isPartialCorrect(answers) {
      return answers.some(x => x.is_valid && x.is_correct);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/fonts.scss';

.not-passed {
  color: $red;
}

.passed {
  color: $green;
}

.single-test {
  padding: 0 0 30px;
  margin-top: -9px;

  .head {
    display: flex;
    justify-content: space-between;
    white-space: nowrap;
    color: #233346;
    margin: 0 0 24px;

    .info {
      background-color: transparent !important;
      border-color: transparent !important;
      display: flex;
      align-items: center;
      .count {
        display: inline-block;
        color: #9faab6;
        margin-right: 17px;
        max-width: 100px;
        overflow: hidden;
        vertical-align: bottom;
        text-overflow: ellipsis;
      }
    }
  }

  .tabs {
    margin-top: 17px;
    display: flex;
    flex-wrap: wrap;
    width: 100%;

    .tab {
      box-sizing: border-box;
      width: 59px;
      height: 40px;
      border: 1px solid $grey-light;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      @include mixin-text-main;
      transition: background 0.3s;

      &.correct {
        color: $green;
      }

      &.incorrect {
        color: $red;
      }

      &.partial-correct {
        color: $orange;
      }

      &.no-correct-answer {
        color: $black;
      }

      &.selected {
        background: $grey-lighter;
        transition: background 0.3s;
      }
    }
  }
}

.anticheating-icon {
  width: 1.5rem;
  height: 1.5rem;
  margin-right: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.duration-block {
  margin-bottom: 15px;
}
</style>
